import React, {useEffect, useState, useMemo, useCallback} from 'react'
import {useParams} from 'react-router-dom'
import axios from 'axios'
import {toast} from 'react-toastify'
import moment from 'moment'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {ALL_READINGS_OF_PATIENTS_HEADERS} from '../../../../constants/RoleConstants'
import BloodPressureChart from '../charts/BloodPressureChart'
import LatestVitalsCard from './LatestVitalsCard'
import DataTableList from '../DataTableList'
import BloodPressureAlerts from '../profile-detail/BloodPressureAlerts'
import BillingSection from './BillingSection'
import ManualBPEntryForm from '../forms/ManualBPEntryForm'
import {useReduxAuth} from '../../../auth'
import {currentMonthYear} from '../../helper/helperFunction'
import helperFunctions from '../../../../helpers/helpers'
// Redux imports for refreshing patient data after BP reading
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../../../../../store/store'
import { refreshPatientAfterDeviceChange } from '../../../../../store/slices/patientSlice'
import { USER_ROLES } from '../../../../constants/StatusConstants'
import { useMADoctorDropdown } from '../../context/MADoctorDropdownContext'

interface Reading {
  sys: number
  dia: number
  pulse: number
  reading_taken_at: string
  bp_category: string
}

interface VitalsData {
  systolic: number | null
  diastolic: number | null
  pulse: number | null
  bp_category: string
  recordedAt: string
}

interface AverageVitals {
  systolic: number | null
  diastolic: number | null
  pulse: number | null
}

const PatientHealthMeasurement: React.FC = () => {
  const {currentUser} = useReduxAuth()
  const dispatch = useDispatch<AppDispatch>()
  const roleId = currentUser?.role_id
  
  // MA Doctor Dropdown
  const maDropdown = useMADoctorDropdown()
  const maSelectedDoctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.selectedDoctorId : undefined
  const doctorId = currentUser.id
  const notifyError = useCallback((msg: string) => {
  toast.error(msg)
}, [])
  const {patientId} = useParams<{patientId: string}>()

  const [chartReadings, setChartReadings] = useState<Reading[]>([])
  const [tableReadings, setTableReadings] = useState<Reading[]>([])
  const [avgVitals, setAvgVitals] = useState<AverageVitals | null>(null)
  const [_isLoading, setIsLoading] = useState(false)
  const [showManualForm, setShowManualForm] = useState(false)

  const endDate = moment().add(1, 'days').format('YYYY-MM-DD')
  const startDate = moment().subtract(29, 'days').format('YYYY-MM-DD')

  // Fetch last-30-days for chart & averages
  const fetchChartAndAvg =  useCallback(async () => {
    setIsLoading(true)
    try {
      // Build query params - include doctor_id for MA
      const doctorIdParam = roleId === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId
        ? `doctor_id=${maSelectedDoctorId}`
        : null
      const [chartResp, avgResp] = await Promise.all([
        axios.get<{status: string; message: string; data: {readings: Reading[]}}>(
          API_ENDPOINTS.GET_PATIENT_LAST_30_DAYS_READINGS(patientId!, currentMonthYear, doctorIdParam)
        ),
        axios.get<{status: string; data: AverageVitals}>(
          API_ENDPOINTS.GET_PATIENT_AVERAGE_VITALS(patientId!, doctorIdParam)
        ),
      ])
      const readings = chartResp.data.data.readings
      if (readings.length > 0) {
        setChartReadings(readings)
      } else {
        setChartReadings(helperFunctions.getDefautSettings(patientId))
      }
      setAvgVitals(avgResp.data.data)
    } catch (err: any) {
      notifyError(err?.response?.data?.message ?? 'Failed to load chart or averages')
    } finally {
      setIsLoading(false)
    }
  }, [patientId, notifyError, roleId, maSelectedDoctorId])

  // Fetch filtered range for table & latest card
  const fetchTableReadings = useCallback(async () => {
    setIsLoading(true)
    try {
      // Build query params - include doctor_id for MA
      const doctorIdParam = roleId === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId
        ? `doctor_id=${maSelectedDoctorId}`
        : null
      const resp = await axios.get<{data: {rangeReadings: any[]}}>(
        API_ENDPOINTS.GET_PATIENT_READINGS_IN_RANGE(
          patientId!, startDate, endDate, doctorIdParam
        )
      )
      const tbl = resp.data.data.rangeReadings.map((r) => ({
        sys: r.sys,
        dia: r.dia,
        pulse: r.pulse,
        reading_taken_at: r.reading_taken_at,
        bp_category: r.bp_category,
      }))
      setTableReadings(tbl)
    } catch (err: any) {
      notifyError(err?.response?.data?.message ?? 'Failed to load table data')
    } finally {
      setIsLoading(false)
    }
  }, [patientId, startDate, endDate, notifyError, roleId, maSelectedDoctorId])

  useEffect(() => {
    fetchChartAndAvg()
    fetchTableReadings()
  }, [fetchChartAndAvg, fetchTableReadings])

  const latestVitalsData = useMemo<VitalsData[]>(() => {
    if (!chartReadings.length) return []
    const latest = chartReadings.reduce((prev, curr) => {
      return new Date(curr.reading_taken_at).getTime() >= new Date(prev.reading_taken_at).getTime()
        ? curr
        : prev
    }, chartReadings[0])
    return [
      {
        systolic: latest.sys,
        diastolic: latest.dia,
        pulse: latest.pulse,
        bp_category: latest.bp_category,
        recordedAt: latest.reading_taken_at,
      },
    ]
  }, [chartReadings])

  const handleManualSuccess = () => {
    setShowManualForm(false)
    fetchChartAndAvg()
    fetchTableReadings()
    // Refresh patient data for PatientDetailBadges after BP reading submission
    if (patientId) {
      const doctorIdForRefresh = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maSelectedDoctorId : undefined
      dispatch(refreshPatientAfterDeviceChange({ patientUuid: patientId, doctorId: doctorIdForRefresh }))
    }
  }
  if (!patientId) {
    // you could show a spinner, placeholder, or just nothing
    return <div>Loading...</div>
  }

  return (
    <>
      <div className='row g-4 mb-4'>
        <div className='col-12'>
          <div className='vitals-container'>
            <div className='vitals-item'>
              <BloodPressureAlerts patientId={patientId!} />
            </div>
            <div className='vitals-item'>
              <LatestVitalsCard
                data={latestVitalsData}
                patientId={patientId!}
                avgVitals={avgVitals}
              />
            </div>
          </div>
        </div>
      </div>
      <BillingSection patientUUID={patientId} monthYear={currentMonthYear} />
      {showManualForm && (
        <ManualBPEntryForm
          patientUUID={patientId!}
          doctorId={doctorId.toString()}
          onSuccess={handleManualSuccess}
        />
      )}
      <div className='d-flex flex-column gap-4'>
        <div className='border p-4 rounded-4 bg-white'>
          <div className='d-flex align-items-center gap-2'>
            <h3>Blood Pressure </h3>
            <span>mmHg (Last 30 Days)</span>
          </div>
          <BloodPressureChart data={chartReadings} />
        </div>
      </div>
      {/* Table & Filter */}
      <div className='mt-4'>
        <h3>Blood Pressure Readings</h3>
        <DataTableList
          showHeader={false}
          columns={ALL_READINGS_OF_PATIENTS_HEADERS}
          data={tableReadings}
          showAddButton={false}
        />
      </div>
    </>
  )
}

export default PatientHealthMeasurement
